import { Injectable, isDevMode, signal } from '@angular/core';
import { SseService } from '../services/sse.service';
import { AuthStore } from './auth.store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { API } from '../services/constants';
import { Subscription } from 'rxjs';
import { EventBusService } from '../services/eventBus.service';
import { EVENTS } from '../utils/constants';
import { BankAccountsStore } from './bank-accounts.store';
import { PaymentReceivedSheetComponent } from '../pages/deposit/qr/payment-received-sheet.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { PollingService } from '../services/polling.service';
import { TransferService } from '../services/transfer.service';
import { TransactionTypeEnum } from './types';
import { ToastService } from '../services/toast.service';

const timeouts = { 0: 1000, 1: 2000, 2: 5000, 3: 10000, 4: 10000, 5: 10000 };

@Injectable({
  providedIn: 'root'
})
export class TransferStore {
  subscription: Subscription;
  transfersEvents = signal({});

  retryCount = 0;
  retryTimeout = 0;
  shouldRetry = false;
  loadingTransactionInfo = signal(false);
  pollTransactionInfo = signal(null);

  constructor(
    private pollingService: PollingService,
    private sseService: SseService,
    private authStore: AuthStore,
    private bottomSheet: MatBottomSheet,
    private matSnackBar: MatSnackBar,
    private eventBusService: EventBusService,
    private bankAccountStore: BankAccountsStore,
    private transferService: TransferService,
    private toast: ToastService
  ) {
    this.eventBusService.on(EVENTS.unsubscribeEvents).subscribe((data) => {
      this.unsubscribeTransferEvents();
    });
    this.subscribeTransferEvents();
  }

  subscribeTransferEvents() {
    if (this.authStore.isAuthenticated()) {
      const subscription = this.sseService
        .connect(`${environment.api_url}/${API.transferEventsStream}`)
        .subscribe({
          next: async (data) => {
            if (isDevMode()) {
              console.log('New transfer event received:', data);
            }

            if (data.type === 'TRANSACTION_COMPLETED') {
              await this.bankAccountStore.updateAccountsBalance();
              this.transfersEvents.set({ ...this.transfersEvents(), [data?.reference]: data });
            
							if (data.transactionTypeType == TransactionTypeEnum.PAYMENT_TROPIPAY) {
                this.toast.success('Se pagó su solicitud a Tropipay');
              } else if (data.transactionTypeType == TransactionTypeEnum.PAYMENT_EPCARD) {
                this.toast.success('Se pagó su solicitud a Tarjeta VISA Epcard');
              } else if (
                data.role === 'sender' ||
                data.transactionTypeType === TransactionTypeEnum.OWNER_TRANSFER
              ) {
                this.matSnackBar.open('La transferencia se completó exitosamente', 'Ok', {
                  duration: 3000,
                  panelClass: 'mat-snack-success'
                });
              } else if (
                data.role === 'receiver' &&
                data.transactionTypeType !== TransactionTypeEnum.OWNER_TRANSFER
              ) {
                const audio = new Audio();
                audio.src = '../assets/audio/deposit_sound.mp3';
                audio.load();
                audio.play();

                this.bottomSheet.open(PaymentReceivedSheetComponent, {
                  data,
                  panelClass: 'app-sheet-container'
                });
              }
            } else if (data.type === 'TRANSACTION_FAILED') {
              this.transfersEvents.set({ ...this.transfersEvents(), [data?.reference]: data });

              this.matSnackBar.open('La transferencia falló', 'Ok', {
                duration: 3000,
                panelClass: 'mat-snack-error'
              });
            } else if (data.type === 'QR_SCANNED') {
              this.matSnackBar.open('El código QR fue escaneado', 'Ok', {
                duration: 3000,
                panelClass: 'mat-snack-info'
              });
            }

            this.eventBusService.emit(EVENTS.transferEventReceived, data);
          },
          error: (err) => {
            this.shouldRetry = this.retryCount < 50;
            this.retryCount++;
            this.retryTimeout = timeouts[this.retryCount] ?? 10000;
            if (this.shouldRetry) {
              setTimeout(() => {
                this.subscribeTransferEvents();
              }, this.retryTimeout);
            }

            if (environment.env !== 'production') {
              console.error('SSE Error:', err);
            }
          }
        });

      this.subscription = subscription;
    }
  }

  unsubscribeTransferEvents() {
    this.subscription?.unsubscribe?.();
  }

  startPollingTransaction(
    reference: string,
    onConfirmed: (reference: string, serverData?: any) => void,
    onFailed: (reference: string, serverData?: any) => void
  ) {
    const stop = this.pollingService.startPolling('new-transaction-status', {
      shouldStop: (data) => {
        return (data as any)?.status === 'COMPLETED' || (data as any)?.status === 'FAILED';
      },
      action: async () => {
        if (this.loadingTransactionInfo()) return this.pollTransactionInfo();
        this.loadingTransactionInfo.set(true);
        try {
          // silentError + background: esta petición la dispara el polling, no el usuario.
          // Sin ellas cada vuelta sacaría un snackbar de error y un 403 llevaría a /404.
          const result = await this.transferService.getTransaction(reference, {
            silentError: true,
            background: true
          });
          const status = result?.status ?? (result as any)?.error?.statusCode;

          // 404 = el core todavía no ha persistido la transacción. Es el estado normal de
          // los primeros segundos tras lanzar la transferencia: seguir esperando.
          if (status === 404) return this.pollTransactionInfo();

          // 403 = la referencia no pertenece a ninguna cuenta del usuario. Reintentar no
          // va a cambiar la respuesta, así que se corta en vez de agotar los reintentos.
          if (status === 403) {
            onFailed(reference, null);
            stop();
            return this.pollTransactionInfo();
          }

          if (result?.data?.status === 'COMPLETED') {
            onConfirmed(reference, result.data);
            stop();
          } else if (result?.data?.status === 'FAILED') {
            onFailed(reference, result.data);
            stop();
          }
          return result.data;
        } finally {
          this.loadingTransactionInfo.set(false);
        }
      },
      onStop: (data) => {
        if ((data as any)?.transaction?.status !== 'COMPLETED') {
          onFailed(reference, (data as any)?.transaction);
        }
      },
      intervalTime: 3000
    });
  }
}
